import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { useSpin } from '@/components/SpinContext.jsx';
import { useToken } from '@/common/helper';
import {
  DeleteVendorConnection,
  GetVendorConnections,
  SaveVendorConnection,
  VerifyVendorConnection,
} from '@/apis/vendorConnections';
import type {
  SaveVendorConnectionPayload,
  VendorConnectionData,
} from '@/apis/vendorConnections';
import type { SaveOutcome } from '../types';

// The API functions reject with the axios response, so the server's message
// sits at `data.data.message`. One cast to the narrowest shape, since the
// rejection is typed `unknown`.
const errorMessage = (error: unknown, fallback: string): string =>
  (error as { data?: { data?: { message?: string } } })?.data?.data?.message ||
  fallback;

// A rejected request carries the axios response, whose `config.data` is the
// request body (the credentials just typed), so log only its status and the
// server's message, never the object.
const logFailure = (context: string, error: unknown): void => {
  const response = error as { status?: number } | undefined;
  console.error(context, response?.status, errorMessage(error, ''));
};

export interface VendorConnections {
  vendors: VendorConnectionData[];
  loading: boolean;
  loadFailed: boolean;
  saving: boolean;
  editing: VendorConnectionData | null;
  setEditing: (vendor: VendorConnectionData | null) => void;
  disconnectTarget: VendorConnectionData | null;
  setDisconnectTarget: (vendor: VendorConnectionData | null) => void;
  handleSave: (payload: SaveVendorConnectionPayload) => Promise<SaveOutcome>;
  handleConfirmDisconnect: () => void;
}

/** The company's quoting/booking vendor connections and the actions on them
 * (connect/edit with an automatic test, disconnect). Shared by Settings > Quoting
 * vendors and the Add drawer of Transport providers. With `enabled` false it
 * stays idle and makes no request. */
export const useVendorConnections = (enabled = true): VendorConnections => {
  const { token } = useToken();
  const { showSpin, hideSpin } = useSpin();

  const [vendors, setVendors] = useState<VendorConnectionData[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [editing, setEditing] = useState<VendorConnectionData | null>(null);
  const [disconnectTarget, setDisconnectTarget] =
    useState<VendorConnectionData | null>(null);
  const requestId = useRef(0);
  // A ref, so a second submit in the same tick is refused before state updates.
  const savingRef = useRef(false);

  const fetchVendors = useCallback(
    (silent = false): void => {
      const thisRequest = ++requestId.current;
      if (!silent) setLoading(true);
      GetVendorConnections(token)
        .then((res) => {
          if (thisRequest !== requestId.current) return;
          const data = res?.data?.data;
          if (data?.is_success) {
            setVendors(data.data?.vendors ?? []);
            setLoadFailed(false);
          } else {
            setLoadFailed(true);
            message.error(data?.message || 'Failed to fetch vendors.');
          }
        })
        .catch((error) => {
          if (thisRequest !== requestId.current) return;
          setLoadFailed(true);
          logFailure('Error fetching vendor connections:', error);
        })
        .finally(() => {
          if (thisRequest === requestId.current) setLoading(false);
        });
    },
    [token]
  );

  useEffect(() => {
    if (enabled) {
      fetchVendors();
      return;
    }
    // access revoked or session changed: drop what was fetched (it now holds secrets)
    requestId.current++;
    setVendors([]);
    setEditing(null);
    setDisconnectTarget(null);
    setLoading(false);
  }, [enabled, fetchVendors]);

  const reportVerified = (verified: VendorConnectionData | undefined): void => {
    if (verified?.status === 'synced') {
      message.success('Connection verified.');
    } else if (verified?.status === 'error') {
      message.warning(
        verified.status_detail || 'The vendor rejected these credentials.'
      );
    }
  };

  const verify = async (vendor: string): Promise<void> => {
    try {
      const res = await VerifyVendorConnection(token, vendor);
      reportVerified(res?.data?.data?.data);
    } catch (error) {
      logFailure('Error verifying vendor connection:', error);
      message.error(
        errorMessage(error, 'Could not reach the vendor. Try again shortly.')
      );
    }
  };

  const handleSave = async (
    payload: SaveVendorConnectionPayload
  ): Promise<SaveOutcome> => {
    if (!editing || savingRef.current) return { ok: false };
    savingRef.current = true;
    setSaving(true);
    showSpin();
    try {
      const res = await SaveVendorConnection(token, editing.vendor, payload);
      const data = res?.data?.data;
      if (!data?.is_success) {
        const failure = data?.message || 'Failed to save the connection.';
        message.error(failure);
        return { ok: false, message: failure };
      }
      message.success(data.message || 'Connection saved.');
      const vendor = editing.vendor;
      setEditing(null);
      // Changed credentials come back pending; test them straight away so the
      // admin sees whether the vendor accepts them. Changing only an optional
      // field leaves a verified connection verified, so it costs no vendor login.
      if (data.data?.status === 'pending') await verify(vendor);
      fetchVendors(true);
      return { ok: true };
    } catch (error) {
      logFailure('Error saving vendor connection:', error);
      const failure = errorMessage(error, 'Failed to save the connection.');
      message.error(failure);
      return { ok: false, message: failure };
    } finally {
      savingRef.current = false;
      setSaving(false);
      hideSpin();
    }
  };

  const handleConfirmDisconnect = (): void => {
    if (!disconnectTarget || disconnecting) return;
    const target = disconnectTarget;
    setDisconnecting(true);
    DeleteVendorConnection(token, target.vendor)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          message.success(data.message || 'Vendor disconnected.');
          setDisconnectTarget(null);
          fetchVendors(true);
        } else {
          message.error(data?.message || 'Failed to disconnect.');
        }
      })
      .catch((error) => {
        // The interceptor has already toasted the failure.
        logFailure('Error disconnecting vendor:', error);
      })
      .finally(() => setDisconnecting(false));
  };

  return {
    vendors,
    loading,
    loadFailed,
    saving,
    editing,
    setEditing,
    disconnectTarget,
    setDisconnectTarget,
    handleSave,
    handleConfirmDisconnect,
  };
};
